/**
 * Self-test suite for the encrypted-local-backup feature (web).
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/BackupSelfTest.swift` in shape and
 * intent. Each group targets one boundary in the seal/open pipeline so a
 * single FAIL points at the broken layer:
 *
 *   A. Header serialise/parse  (BackupHeader.canonicalHeaderBytes + parseFileBytes)
 *   B. Passphrase policy       (validatePassphrase)
 *   C. Seal/open round-trips   (BackupCipher.seal / .open)
 *   D. AEAD-binding + tamper   (header AAD + ciphertext byte-flips)
 *   E. Resource-exhaustion     (100MB cap before decrypt)
 *   F. Bundle envelope         (encodeBundle / decodeBundle)
 *
 * The suite hits the pure-crypto path only — no app-store writes, no
 * network — so it is safe to run from a debug menu on a real install.
 *
 * NOTE: PBKDF2 with 600 000 iterations is intentionally slow (~0.1s+ per
 * derivation depending on host). Round-trip cases that seal then open run
 * the KDF twice. Total suite wall-time should stay under a few seconds
 * even on modest hardware; we deliberately avoid huge plaintexts in any
 * case except the 100MB-cap test (which never calls the KDF — the cap
 * fires before the decrypt is invoked).
 *
 * Registered with `SelfTestRunner` at module load; auto-discovered by
 * `selftest/index.ts` via `import.meta.glob("../**\/*SelfTest.ts")`.
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  BACKUP_MAGIC,
  BACKUP_NONCE_BYTES,
  BACKUP_SALT_BYTES,
  BackupError,
  canonicalHeaderBytes,
  encodeFileBytes,
  parseFileBytes,
  type BackupHeader,
} from "./BackupHeader";
import { validatePassphrase } from "./BackupPassphrasePolicy";
import { MAX_PLAINTEXT_BYTES, open, seal } from "./BackupCipher";
import { decodeBundle, encodeBundle } from "./BackupBundle";

// ---------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------

/** A passphrase that always satisfies `validatePassphrase`. */
const TEST_PASSPHRASE = "backup-test-passphrase-correct-horse-battery-staple";

/** A different passphrase used to verify the wrong-passphrase rejection. */
const WRONG_PASSPHRASE = "different-passphrase-7777777";

function sampleHeader(): BackupHeader {
  return {
    magic: BACKUP_MAGIC,
    kdfAlgorithm: "pbkdf2-sha256-600k",
    kdfSalt: new Uint8Array(BACKUP_SALT_BYTES).fill(0xA1),
    cipherNonce: new Uint8Array(BACKUP_NONCE_BYTES).fill(0x55),
    createdAt: 1715000000,
  };
}

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

// ---------------------------------------------------------------------------
// A. Header serialise/parse
// ---------------------------------------------------------------------------

async function testHeaderCanonicalDeterministic(): Promise<TestCase> {
  const name = "header: canonical bytes deterministic across two calls";
  const h = sampleHeader();
  const a = canonicalHeaderBytes(h);
  const b = canonicalHeaderBytes(h);
  return bytesEqual(a, b)
    ? pass(name)
    : fail(name, `two encodes disagreed (lengths ${a.length}, ${b.length})`);
}

async function testHeaderRoundTripPreservesMagicAndKdf(): Promise<TestCase> {
  const name = "header: serialise + parse roundtrip preserves magic + kdfAlgorithm";
  try {
    const h = sampleHeader();
    const fileBytes = encodeFileBytes(
      h,
      new Uint8Array([1, 2, 3, 4]),
      new Uint8Array(16).fill(0x77),
    );
    const parsed = parseFileBytes(fileBytes);
    const ok =
      parsed.header.magic === h.magic &&
      parsed.header.kdfAlgorithm === h.kdfAlgorithm &&
      parsed.header.createdAt === h.createdAt &&
      bytesEqual(parsed.header.kdfSalt, h.kdfSalt) &&
      bytesEqual(parsed.header.cipherNonce, h.cipherNonce);
    return ok
      ? pass(name)
      : fail(name, `mismatch: ${JSON.stringify({
          magic: parsed.header.magic,
          kdf: parsed.header.kdfAlgorithm,
        })}`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testHeaderParseRejectsTruncated(): Promise<TestCase> {
  const name = "header: parse rejects truncated input with unsupportedMagic";
  try {
    parseFileBytes(new Uint8Array([0x6B, 0x61])); // "ka"
    return fail(name, "parse succeeded");
  } catch (e) {
    if (e instanceof BackupError && e.kind === "unsupportedMagic") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testHeaderParseRejectsWrongMagic(): Promise<TestCase> {
  const name = "header: parse rejects wrong magic with unsupportedMagic";
  const bogus = new TextEncoder().encode("not-a-backup-file-of-any-kind");
  try {
    parseFileBytes(bogus);
    return fail(name, "parse succeeded");
  } catch (e) {
    if (e instanceof BackupError && e.kind === "unsupportedMagic") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testHeaderParseRejectsUnknownKdf(): Promise<TestCase> {
  const name = "header: parse rejects unknown kdfAlgorithm with unsupportedKdfAlgorithm";
  // Hand-build the JSON body so we can put an unknown algorithm string in.
  const saltB64 = btoa(String.fromCharCode(...new Uint8Array(32).fill(0x11)));
  const nonceB64 = btoa(String.fromCharCode(...new Uint8Array(12).fill(0x22)));
  const ctB64 = btoa(String.fromCharCode(...new Uint8Array(16).fill(0x33)));
  const tagB64 = btoa(String.fromCharCode(...new Uint8Array(16).fill(0x44)));
  const json =
    `{"authTag":"${tagB64}","ciphertext":"${ctB64}","header":{` +
    `"cipherNonce":"${nonceB64}","createdAt":1715000000,` +
    `"kdfAlgorithm":"argon2id-future-N1","kdfSalt":"${saltB64}",` +
    `"magic":"${BACKUP_MAGIC}"}}`;
  const enc = new TextEncoder();
  const file = new Uint8Array(enc.encode(BACKUP_MAGIC).length + enc.encode(json).length);
  file.set(enc.encode(BACKUP_MAGIC), 0);
  file.set(enc.encode(json), enc.encode(BACKUP_MAGIC).length);
  try {
    parseFileBytes(file);
    return fail(name, "parse succeeded");
  } catch (e) {
    if (e instanceof BackupError && e.kind === "unsupportedKdfAlgorithm") {
      return pass(name, `detail=${e.detail}`);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// B. Passphrase policy
// ---------------------------------------------------------------------------

function testPolicyTooShort(): TestCase {
  const name = "policy: 5 chars rejected with reason 'tooShort'";
  const v = validatePassphrase("abcde");
  return v.ok === false && v.reason === "tooShort"
    ? pass(name)
    : fail(name, `got ${JSON.stringify(v)}`);
}

function testPolicyTooLong(): TestCase {
  const name = "policy: 129 chars rejected with reason 'tooLong'";
  const v = validatePassphrase("a".repeat(129));
  return v.ok === false && v.reason === "tooLong"
    ? pass(name)
    : fail(name, `got ${JSON.stringify(v)}`);
}

function testPolicyAllWhitespace(): TestCase {
  const name = "policy: all-whitespace rejected with reason 'allWhitespace'";
  const v = validatePassphrase("       ");
  return v.ok === false && v.reason === "allWhitespace"
    ? pass(name)
    : fail(name, `got ${JSON.stringify(v)}`);
}

function testPolicyEmpty(): TestCase {
  const name = "policy: empty rejected with reason 'empty'";
  const v = validatePassphrase("");
  return v.ok === false && v.reason === "empty"
    ? pass(name)
    : fail(name, `got ${JSON.stringify(v)}`);
}

function testPolicyValid(): TestCase {
  const name = "policy: 'correct-passphrase-99' accepted with ok=true";
  const v = validatePassphrase("correct-passphrase-99");
  return v.ok === true ? pass(name) : fail(name, `got ${JSON.stringify(v)}`);
}

// ---------------------------------------------------------------------------
// C. Cipher round-trips
// ---------------------------------------------------------------------------

async function testCipherRoundTrip(): Promise<TestCase> {
  const name = "cipher: seal then open with same passphrase recovers identical plaintext";
  try {
    const plaintext = new TextEncoder().encode("the quick brown fox jumps over the lazy dog");
    const file = await seal(TEST_PASSPHRASE, plaintext);
    const recovered = await open(TEST_PASSPHRASE, file);
    return bytesEqual(recovered, plaintext)
      ? pass(name)
      : fail(name, `plaintext mismatch (recovered len=${recovered.length}, want=${plaintext.length})`);
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

async function testCipherWrongPassphraseFails(): Promise<TestCase> {
  const name = "cipher: open with wrong passphrase fails with authFailed";
  try {
    const plaintext = new TextEncoder().encode("secret");
    const file = await seal(TEST_PASSPHRASE, plaintext);
    try {
      await open(WRONG_PASSPHRASE, file);
      return fail(name, "open() succeeded with wrong passphrase");
    } catch (e) {
      if (e instanceof BackupError && e.kind === "authFailed") {
        return pass(name);
      }
      return fail(name, `wrong error: ${e}`);
    }
  } catch (e) {
    return fail(name, `seal threw: ${e}`);
  }
}

async function testCipherFreshSaltNoncePerSeal(): Promise<TestCase> {
  const name =
    "cipher: two seals with same passphrase + plaintext produce DIFFERENT ciphertext";
  try {
    const plaintext = new TextEncoder().encode("repeat-input");
    const f1 = await seal(TEST_PASSPHRASE, plaintext);
    const f2 = await seal(TEST_PASSPHRASE, plaintext);
    return !bytesEqual(f1, f2)
      ? pass(name)
      : fail(name, "files were byte-identical (salt/nonce reused?)");
  } catch (e) {
    return fail(name, `threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// D. AEAD-binding + tamper
// ---------------------------------------------------------------------------

async function testCipherTamperedCiphertextFails(): Promise<TestCase> {
  const name = "cipher: tampering one byte of ciphertext causes open to fail";
  try {
    const plaintext = new TextEncoder().encode("ciphertext-tamper-test");
    const file = await seal(TEST_PASSPHRASE, plaintext);
    const bundle = parseFileBytes(file);
    if (bundle.ciphertext.length === 0) {
      return fail(name, "ciphertext empty");
    }
    const tampered = new Uint8Array(bundle.ciphertext);
    tampered[0] ^= 0x01;
    const tamperedFile = encodeFileBytes(bundle.header, tampered, bundle.authTag);
    try {
      await open(TEST_PASSPHRASE, tamperedFile);
      return fail(name, "open() succeeded on tampered ciphertext");
    } catch (e) {
      if (e instanceof BackupError && e.kind === "authFailed") {
        return pass(name);
      }
      return fail(name, `wrong error: ${e}`);
    }
  } catch (e) {
    return fail(name, `seal/parse threw: ${e}`);
  }
}

async function testCipherTamperedHeaderAadFails(): Promise<TestCase> {
  // The header is bound as AES-GCM AAD via canonicalHeaderBytes. Flipping
  // any byte of the salt (which lives inside the AAD region) MUST cause
  // the GCM open to fail — this is the only way to prove the AAD binding
  // is actually enforced by `crypto.subtle.decrypt`.
  const name =
    "cipher: tampering header AAD region (salt) causes open to fail (proves AAD binding)";
  try {
    const plaintext = new TextEncoder().encode("aad-binding-test");
    const file = await seal(TEST_PASSPHRASE, plaintext);
    const bundle = parseFileBytes(file);
    const newSalt = new Uint8Array(bundle.header.kdfSalt);
    newSalt[0] ^= 0xFF;
    const tamperedHeader: BackupHeader = {
      ...bundle.header,
      kdfSalt: newSalt,
    };
    const tamperedFile = encodeFileBytes(tamperedHeader, bundle.ciphertext, bundle.authTag);
    try {
      await open(TEST_PASSPHRASE, tamperedFile);
      return fail(name, "open() succeeded on tampered header");
    } catch (e) {
      // The salt flip causes the KDF to derive a different key, which in
      // turn causes the GCM tag check to fail — surfaced as authFailed.
      // (If a future change moves the salt outside the KDF inputs, this
      // test will still catch a missing AAD binding because the AAD bytes
      // would change.)
      if (e instanceof BackupError && e.kind === "authFailed") {
        return pass(name);
      }
      return fail(name, `wrong error: ${e}`);
    }
  } catch (e) {
    return fail(name, `seal/parse threw: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// E. Resource-exhaustion (100MB cap)
// ---------------------------------------------------------------------------

async function testCipherOversizeFileRejectedBeforeDecrypt(): Promise<TestCase> {
  // Build a buffer larger than 100MB. The first ~17 bytes match the magic
  // so `parseFileBytes` would proceed if the cap didn't fire first.
  // We rely on `open` rejecting on TOTAL file length BEFORE touching the
  // JSON parser — that's the DoS-protection invariant being tested.
  const name =
    "cipher: 100MB+1 sealed input rejected with payloadOverLimit (no decrypt)";
  const big = new Uint8Array(MAX_PLAINTEXT_BYTES + 1);
  // Fill the first 17 bytes with the magic so we know the cap fired BEFORE
  // the magic check (proving it's a size check, not a structural one).
  const magicBytes = new TextEncoder().encode(BACKUP_MAGIC);
  big.set(magicBytes, 0);
  try {
    await open(TEST_PASSPHRASE, big);
    return fail(name, "open() succeeded on oversize input");
  } catch (e) {
    if (e instanceof BackupError && e.kind === "payloadOverLimit") {
      return pass(name, `detail=${e.detail}`);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// F. Bundle envelope
// ---------------------------------------------------------------------------

function testBundleRoundTrip(): TestCase {
  const name = "bundle: encode + decode roundtrip preserves shape";
  const input = {
    schemaVersion: 1 as const,
    exportedAt: 1715000000,
    contents: {
      identityKey: "fake-base64-bytes",
      sessions: [{ id: "a", state: "b" }, { id: "c", state: "d" }],
      settings: { theme: "dark" },
    },
  };
  const encoded = encodeBundle(input);
  const decoded = decodeBundle(encoded);
  const ok =
    decoded.schemaVersion === input.schemaVersion &&
    decoded.exportedAt === input.exportedAt &&
    JSON.stringify(decoded.contents) === JSON.stringify(input.contents);
  return ok
    ? pass(name)
    : fail(name, `mismatch: schemaVersion=${decoded.schemaVersion} exportedAt=${decoded.exportedAt}`);
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Encrypted local backup (header + seal + roundtrip)",
  isVerified: true,
  run: async (): Promise<ReturnType<typeof buildSuiteResult>> => {
    const cases: TestCase[] = [
      // A. Header serialise/parse
      await testHeaderCanonicalDeterministic(),
      await testHeaderRoundTripPreservesMagicAndKdf(),
      await testHeaderParseRejectsTruncated(),
      await testHeaderParseRejectsWrongMagic(),
      await testHeaderParseRejectsUnknownKdf(),

      // B. Passphrase policy
      testPolicyTooShort(),
      testPolicyTooLong(),
      testPolicyAllWhitespace(),
      testPolicyEmpty(),
      testPolicyValid(),

      // C. Cipher round-trips
      await testCipherRoundTrip(),
      await testCipherWrongPassphraseFails(),
      await testCipherFreshSaltNoncePerSeal(),

      // D. AEAD-binding + tamper
      await testCipherTamperedCiphertextFails(),
      await testCipherTamperedHeaderAadFails(),

      // E. Resource-exhaustion
      await testCipherOversizeFileRejectedBeforeDecrypt(),

      // F. Bundle envelope
      testBundleRoundTrip(),
    ];
    return buildSuiteResult(cases);
  },
});
